import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { ArrowLeft, Plus, Trash2, Pencil, Mail, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

function PersonDialog({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [aliases, setAliases] = useState((initial?.aliases || []).join(", "));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        email: email.trim() || null,
        aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
      };
      if (isEdit) await api.patch(`/people/${initial.id}`, body);
      else await api.post("/people", body);
      toast.success(isEdit ? "Person updated" : "Person added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-sand-300" data-testid="person-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            {isEdit ? "Edit person" : "Add person"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="label-tag block mb-1">Name</label>
            <input className="swiss-input text-sm" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Adam Conrad Ahern"
              data-testid="person-name" />
          </div>
          <div>
            <label className="label-tag block mb-1">Email</label>
            <input type="email" className="swiss-input text-sm" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adam@example.com"
              data-testid="person-email" />
            <p className="text-xs text-sand-500 mt-1">Used for ICS calendar invites (ATTENDEE field).</p>
          </div>
          <div>
            <label className="label-tag block mb-1">Aliases (comma-separated)</label>
            <input className="swiss-input text-sm" value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="Adam Ahern, A. C. Ahern, AHERN/ADAM"
              data-testid="person-aliases" />
            <p className="text-xs text-sand-500 mt-1">Alternate spellings the LLM might use. Auto-collected over time.</p>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="person-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-swiss btn-primary text-sm" data-testid="person-save">
            {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function People() {
  const [people, setPeople] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/people");
      setPeople(data);
    } catch (e) { setPeople([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}? This will not delete reservations, but they'll lose the email link.`)) return;
    try { await api.delete(`/people/${p.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <main className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16" data-testid="people-page">
        <Link to="/home" className="inline-flex items-center text-sm text-sand-600 hover:text-ember mb-8" data-testid="people-back">
          <ArrowLeft size={14} className="mr-2" /> Dashboard
        </Link>

        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-tag mb-3">Address book</p>
            <h1 className="heading-h1 text-4xl sm:text-5xl mb-2">People</h1>
            <div className="divider-strong mt-4" />
            <p className="text-sand-600 mt-4 max-w-xl">
              When the LLM extracts a passenger / driver / attendee name, we auto-create an entry here.
              Add an email and they'll be invited as an attendee on every future ICS export that mentions them.
            </p>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="btn-swiss btn-primary text-sm"
            data-testid="add-person-btn"
          >
            <Plus size={14} className="mr-2" /> Add person
          </button>
        </div>

        {people === null ? (
          <div className="space-y-2">{[0,1,2].map(i => <div key={i} className="h-16 bg-sand-100 animate-pulse" />)}</div>
        ) : people.length === 0 ? (
          <div className="rounded-card border border-hairline bg-surface p-8 text-center" data-testid="people-empty">
            <p className="text-sand-600 text-sm">
              No people yet. They'll appear here automatically once you forward your first reservation,
              or you can add them manually.
            </p>
          </div>
        ) : (
          <ul className="rounded-card border border-hairline bg-surface divide-y divide-sand-100" data-testid="people-list">
            {people.map((p) => (
              <li key={p.id} className="px-6 py-4 flex items-center gap-4 hover:bg-sand-50 transition-colors" data-testid={`person-row-${p.id}`}>
                <div className="mt-1">
                  {p.email ? <CheckCircle2 size={16} className="text-[#5E8467]" /> : <AlertCircle size={16} className="text-[#57504A]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sand-900">{p.name}</p>
                  <p className="text-xs text-sand-500 font-mono truncate">
                    {p.email || "no email — won't be invited as attendee"}
                  </p>
                  {p.aliases && p.aliases.length > 0 && (
                    <p className="text-xs text-sand-400 mt-1 truncate">also: {p.aliases.join(" · ")}</p>
                  )}
                </div>
                {p.auto_created && <span className="label-tag text-sand-400">auto</span>}
                <button onClick={() => setEditing(p)} className="p-2 hover:bg-sand-100" data-testid={`edit-person-${p.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(p)} className="p-2 hover:bg-red-50 text-sand-700 hover:text-error" data-testid={`delete-person-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {editing && <PersonDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        {creating && <PersonDialog initial={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      </main>
    </div>
  );
}
